<!-- Drag handle on the title bar -->
<div mat-dialog-title cdkDrag cdkDragHandle cdkDragRootElement=".cdk-overlay-pane" class="debug-title d-flex align-items-center gap-2 py-1 ps-3 pe-2 m-0">
  <mat-icon class="debug-drag-icon flex-shrink-0">drag_indicator</mat-icon>
  <span>Agent Debug — {{ data.agentId }}</span>
  <span class="flex-grow-1"></span>
  <button matIconButton mat-dialog-close matTooltip="Close">
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-dialog-content class="debug-content">
  <mat-tab-group class="debug-tab-group">
    <!-- ── Streaming tab ─────────────────────────────────────── -->
    <mat-tab label="Streaming">
      <div class="tab-layout">
        <div class="d-flex align-items-center gap-2 py-1 flex-shrink-0 flex-wrap">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" style="min-width: 220px">
            <mat-label>Event types</mat-label>
            <mat-select multiple [value]="selectedTypesModel" (selectionChange)="onTypesChange($event)">
              <mat-option value="__ALL__">{{ isAllSelected() ? 'Deselect all' : 'Select all' }}</mat-option>
              @for (t of availableTypes(); track t) {
              <mat-option [value]="t">{{ t }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic" style="min-width: 200px">
            <mat-label>Search</mat-label>
            <input matInput [ngModel]="searchQuery()" (ngModelChange)="onSearchChange($event)" (keydown.enter)="goToNextMatch()" (keydown.shift.enter)="goToPrevMatch()">
            @if (searchMatches().length > 0) {
            <span matSuffix class="d-flex align-items-center gap-1 pe-1">
              <span class="mat-font-body-sm text-body-secondary" style="font-size: 0.7rem">{{ searchMatchIndex() + 1 }}/{{ searchMatches().length }}</span>
              <button matIconButton [style.width.px]="24" [style.height.px]="24" (click)="goToPrevMatch()" matTooltip="Previous (Shift+Enter)">
                <mat-icon [style.font-size.px]="16">keyboard_arrow_up</mat-icon>
              </button>
              <button matIconButton [style.width.px]="24" [style.height.px]="24" (click)="goToNextMatch()" matTooltip="Next (Enter)">
                <mat-icon [style.font-size.px]="16">keyboard_arrow_down</mat-icon>
              </button>
            </span>
            }
          </mat-form-field>
          <span class="mat-font-body-sm text-body-secondary">{{ filteredEvents().length }} / {{ debugService.events().length }}</span>
          <span class="flex-grow-1"></span>
          <button matIconButton
                  [class.auto-clear-active]="debugService.autoClear()"
                  (click)="debugService.autoClear.set(!debugService.autoClear())"
                  matTooltip="Auto-clear on new run or conversation switch">
            <mat-icon>auto_delete</mat-icon>
          </button>
          <button matButton="outlined" (click)="debugService.clear()">
            <mat-icon>delete_sweep</mat-icon>
            Clear
          </button>
        </div>

        <!-- Virtual scroll table -->
        <div class="stream-wrap">
          <div class="stream-row stream-header-row">
            <span class="sc-ts">Time</span>
            <span class="sc-type">Type</span>
            <span class="sc-content">Content</span>
            <span class="sc-action"></span>
          </div>
          <cdk-virtual-scroll-viewport #streamViewport itemSize="34" class="stream-viewport">
            <div class="stream-row" *cdkVirtualFor="let e of filteredEvents(); trackBy: trackById"
              [class.search-highlight]="searchQuery() && e.searchText.includes(searchQuery().toLowerCase())"
            >
              <span class="sc-ts">{{ e.timestamp | date: "HH:mm:ss.SSS" }}</span>
              <span class="sc-type d-flex align-items-center gap-1">
                @if (e.category === 'lifecycle') {
                <mat-chip
                  disableRipple
                  [style.--mdc-chip-container-height.px]="20"
                  [style.--mdc-chip-elevated-container-color]="'var(--mat-sys-tertiary)'"
                  [style.--mdc-chip-label-text-color]="'var(--mat-sys-on-tertiary)'"
                  [style.font-size.rem]="0.52"
                  matTooltip="Lifecycle event">
                  LC
                </mat-chip>
                }
                <mat-chip
                  disableRipple
                  [style.--mdc-chip-container-height.px]="20"
                  [style.--mdc-chip-elevated-container-color]="chipBg(e.type)"
                  [style.--mdc-chip-label-text-color]="chipFg(e.type)"
                  [style.font-size.rem]="0.58"
                  [style.font-family]="'monospace'">
                  {{ e.type }}
                </mat-chip>
              </span>
              <span
                class="sc-content"
                (click)="openDetail(e)"
                [style.cursor]="e.content !== null ? 'pointer' : 'default'"
                [matTooltip]="e.content !== null ? 'Click to expand' : ''"
                matTooltipShowDelay="600">
                {{ summarize(e) }}
              </span>
              <span class="sc-action">
                @if (e.content !== null) {
                <button matIconButton [style.width.px]="24" [style.height.px]="24" matTooltip="Copy" (click)="$event.stopPropagation(); copyContent(e)">
                  <mat-icon [style.font-size.px]="14">content_copy</mat-icon>
                </button>
                }
              </span>
            </div>
          </cdk-virtual-scroll-viewport>
        </div>
      </div>
    </mat-tab>

    <!-- ── Background Jobs tab (pipeline SSE, separate connection per job) ── -->
    <mat-tab label="Background Jobs">
      <div class="tab-layout">
        <div class="d-flex align-items-center gap-2 py-1 flex-shrink-0 flex-wrap">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" style="min-width: 200px">
            <mat-label>Search</mat-label>
            <input matInput [ngModel]="pipelineSearchQuery()" (ngModelChange)="onPipelineSearchChange($event)" (keydown.enter)="goToNextPipelineMatch()" (keydown.shift.enter)="goToPrevPipelineMatch()">
            @if (pipelineSearchMatches().length > 0) {
            <span matSuffix class="d-flex align-items-center gap-1 pe-1">
              <span class="mat-font-body-sm text-body-secondary" style="font-size: 0.7rem">{{ pipelineSearchMatchIndex() + 1 }}/{{ pipelineSearchMatches().length }}</span>
              <button matIconButton [style.width.px]="24" [style.height.px]="24" (click)="goToPrevPipelineMatch()" matTooltip="Previous (Shift+Enter)">
                <mat-icon [style.font-size.px]="16">keyboard_arrow_up</mat-icon>
              </button>
              <button matIconButton [style.width.px]="24" [style.height.px]="24" (click)="goToNextPipelineMatch()" matTooltip="Next (Enter)">
                <mat-icon [style.font-size.px]="16">keyboard_arrow_down</mat-icon>
              </button>
            </span>
            }
          </mat-form-field>
          <span class="mat-font-body-sm text-body-secondary">{{ pipelineEvents().length }} events</span>
          <span class="flex-grow-1"></span>
          <button matButton="outlined" (click)="debugService.clearPipeline()">
            <mat-icon>delete_sweep</mat-icon>
            Clear
          </button>
        </div>

        <!-- Virtual scroll table -->
        <div class="stream-wrap">
          <div class="pl-row pl-header-row">
            <span class="pl-ts">Time</span>
            <span class="pl-job">Job</span>
            <span class="pl-type">Type</span>
            <span class="pl-content">Content</span>
            <span class="pl-action"></span>
          </div>
          <cdk-virtual-scroll-viewport #pipelineViewport itemSize="34" class="stream-viewport">
            <div class="pl-row" *cdkVirtualFor="let e of pipelineEvents(); trackBy: trackPipelineById"
              [class.search-highlight]="pipelineSearchQuery() && e.searchText.includes(pipelineSearchQuery().toLowerCase())"
            >
              <span class="pl-ts">{{ e.timestamp | date: "HH:mm:ss.SSS" }}</span>
              <span class="pl-job" [matTooltip]="e.jobId">{{ e.jobId }}</span>
              <span class="pl-type">
                <mat-chip
                  disableRipple
                  [style.--mdc-chip-container-height.px]="20"
                  [style.--mdc-chip-elevated-container-color]="chipBg(e.type)"
                  [style.--mdc-chip-label-text-color]="chipFg(e.type)"
                  [style.font-size.rem]="0.58"
                  [style.font-family]="'monospace'">
                  {{ e.type }}
                </mat-chip>
              </span>
              <span
                class="pl-content"
                (click)="openPipelineDetail(e)"
                [style.cursor]="e.content !== null ? 'pointer' : 'default'"
                [matTooltip]="e.content !== null ? 'Click to expand' : ''"
                matTooltipShowDelay="600">
                {{ summarizePipeline(e) }}
              </span>
              <span class="pl-action">
                @if (e.content !== null) {
                <button matIconButton [style.width.px]="24" [style.height.px]="24" matTooltip="Copy" (click)="$event.stopPropagation(); copyPipelineContent(e)">
                  <mat-icon [style.font-size.px]="14">content_copy</mat-icon>
                </button>
                }
              </span>
            </div>
          </cdk-virtual-scroll-viewport>
        </div>
      </div>
    </mat-tab>

    <!-- ── State tab ──────────────────────────────────────────── -->
    <mat-tab label="State">
      <div class="state-layout">
        <mat-accordion multi>
          <mat-expansion-panel expanded>
            <mat-expansion-panel-header>
              <mat-panel-title>State Snapshot</mat-panel-title>
            </mat-expansion-panel-header>
            <andypf-json-viewer theme="material-lighter" [data]="latestState()" indent="2" expanded="2" show-toolbar="false" class="json-viewer"></andypf-json-viewer>
          </mat-expansion-panel>

          <mat-expansion-panel expanded>
            <mat-expansion-panel-header>
              <mat-panel-title>Agent Context (map / custom)</mat-panel-title>
            </mat-expansion-panel-header>
            <andypf-json-viewer theme="material-lighter" [data]="latestContext()" indent="2" expanded="2" show-toolbar="false" class="json-viewer"></andypf-json-viewer>
          </mat-expansion-panel>

          <mat-expansion-panel expanded>
            <mat-expansion-panel-header>
              <mat-panel-title>Runtime</mat-panel-title>
            </mat-expansion-panel-header>
            <andypf-json-viewer theme="material-lighter" [data]="runtimeInfo()" indent="2" expanded="2" show-toolbar="false" class="json-viewer"></andypf-json-viewer>
          </mat-expansion-panel>
        </mat-accordion>
      </div>
    </mat-tab>

    <!-- ── Workspace tab (state.workspace_index_snapshot — evidence index) ──── -->
    <mat-tab label="Workspace">
      <div class="tab-layout workspace-tab-layout">
        @if (workspaceIndexSnapshot().length === 0) {
          <div class="d-flex align-items-center gap-2 py-3 text-body-secondary">
            <mat-icon>inventory_2</mat-icon>
            No workspace entries yet
          </div>
        } @else {
          <div class="d-flex flex-column gap-2">
            @for (block of workspaceIndexSnapshot(); track block.id) {
              <app-workspace-index-card [block]="block" />
            }
          </div>
        }
      </div>
    </mat-tab>
  </mat-tab-group>
</mat-dialog-content>
